import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created — welcome!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:block relative bg-emerald-950 emerald-grain">
        <img src="/cars/rangerover.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="relative z-10 p-12 flex flex-col h-full justify-between">
          <Link to="/" className="flex items-center gap-2.5 text-white font-display font-extrabold text-xl">
            <span className="h-9 w-9 rounded-xl overflow-hidden"><img src="/logo.png" alt="logo" className="h-full w-full object-cover scale-[1.18]" /></span>
            Accurate Auto Rentals
          </Link>
          <div>
            <h2 className="font-display text-4xl font-extrabold text-white leading-tight">Join the club.</h2>
            <p className="mt-4 text-emerald-100/70 max-w-sm">Create an account to book premium rentals and track every trip.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
          <h1 className="font-display text-3xl font-extrabold text-emerald-950">Create account</h1>
          <p className="mt-2 text-slate-500 text-sm">Get on the road in under a minute.</p>

          <div className="mt-8 space-y-4">
            <div>
              <Label className="text-slate-600">Full name</Label>
              <Input required value={form.name} onChange={set("name")} data-testid="register-name" className="mt-1.5 h-11 border-slate-200" placeholder="Jane Driver" />
            </div>
            <div>
              <Label className="text-slate-600">Email</Label>
              <Input type="email" required value={form.email} onChange={set("email")} data-testid="register-email" className="mt-1.5 h-11 border-slate-200" placeholder="you@example.com" />
            </div>
            <div>
              <Label className="text-slate-600">Phone <span className="text-slate-400">(optional)</span></Label>
              <Input value={form.phone} onChange={set("phone")} data-testid="register-phone" className="mt-1.5 h-11 border-slate-200" placeholder="+1 555 000 0000" />
            </div>
            <div>
              <Label className="text-slate-600">Password</Label>
              <Input type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="register-password" className="mt-1.5 h-11 border-slate-200" placeholder="Min. 6 characters" />
            </div>
          </div>

          <Button type="submit" disabled={loading} data-testid="register-submit" className="mt-6 w-full h-11 bg-emerald-900 hover:bg-emerald-800 rounded-xl">
            {loading ? <Loader2 className="animate-spin" /> : "Create account"}
          </Button>

          <p className="mt-6 text-center text-sm text-slate-500">
            Already have an account?{" "}
            <Link to="/login" className="text-emerald-700 font-semibold" data-testid="go-login">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
